/// <reference path="../nativewind-augmentation.d.ts" />
// Copyright (c) Ping. All rights reserved.
//
// Auth-screen shell — Figma file gtBsWqJJcI6ZBuBv51RU35
// (Login / Create / Verify / Display-name).
//
// All colours are driven by the design-token classes in the
// `tailwind.preset.cjs` generated from `tokens.json`. Use the
// `light-*` / `dark-*` token names instead of hex literals so the
// auth flow flips cleanly when the user changes theme.

import { useColorScheme } from 'nativewind';
import { type ReactNode } from 'react';
import { Pressable, Text, TextInput, View } from 'react-native';
import { tokens } from '../design-system/tokens';
import { PingLogomark } from '../icons';
import { AuthWallpaper } from './AuthWallpaper';

interface AuthLayoutProps {
	readonly children: ReactNode;
	readonly accessibilityLabel?: string;
}

export function AuthLayout({ children, accessibilityLabel }: AuthLayoutProps): JSX.Element {
	return (
		<View
			accessibilityLabel={accessibilityLabel}
			// Outer dimensions: full viewport. `min-h-screen` (Tailwind
			// 100vh) ensures the wallpaper covers the full window even when
			// the form column is short.
			className="relative w-full min-h-screen items-center justify-center px-xl overflow-hidden bg-light-surface-app dark:bg-dark-surface-app"
		>
			<AuthWallpaper />
			<View className="relative w-[400px] flex-col items-stretch">{children}</View>
		</View>
	);
}

// ── Header ──────────────────────────────────────────────────────────

export interface AuthHeaderProps {
	readonly title: string;
	readonly subtitle?: string;
}

export function AuthHeader({ title, subtitle }: AuthHeaderProps): JSX.Element {
	return (
		<View className="items-center mb-xxl">
			<View className="mb-xl">
				<PingLogomark size={80} />
			</View>
			<Text className="font-sans font-bold text-[20px] leading-[28px] text-light-text-primary dark:text-dark-text-primary text-center">
				{title}
			</Text>
			{subtitle ? (
				<Text className="font-sans font-normal text-[16px] leading-[22px] text-light-text-secondary dark:text-dark-text-secondary text-center mt-xs">
					{subtitle}
				</Text>
			) : null}
		</View>
	);
}

// ── Field (label + slot + optional error) ───────────────────────────

export interface AuthFieldProps {
	readonly label: string;
	readonly children: ReactNode;
	readonly error?: string | null;
}

export function AuthField({ label, children, error }: AuthFieldProps): JSX.Element {
	return (
		<View className="flex-col">
			<Text className="font-sans font-normal text-[14px] leading-[20px] text-light-text-primary dark:text-dark-text-primary mb-xs">
				{label}
			</Text>
			{children}
			{error ? (
				<Text className="font-sans font-normal text-[12px] leading-[17px] text-light-accent-rose dark:text-dark-accent-rose mt-xs">
					{error}
				</Text>
			) : null}
		</View>
	);
}

// ── Input ───────────────────────────────────────────────────────────

export interface AuthInputProps {
	readonly value: string;
	readonly onChangeText: (next: string) => void;
	readonly placeholder?: string;
	readonly hasError?: boolean;
	readonly disabled?: boolean;
	readonly autoFocus?: boolean;
	readonly autoCapitalize?: 'none' | 'words' | 'sentences' | 'characters';
	readonly keyboardType?: 'default' | 'email-address' | 'phone-pad' | 'number-pad';
	readonly maxLength?: number;
	readonly secureTextEntry?: boolean;
	readonly textContentType?:
		| 'emailAddress'
		| 'telephoneNumber'
		| 'oneTimeCode'
		| 'newPassword'
		| 'password'
		| 'name';
	readonly accessibilityLabel?: string;
}

export function AuthInput(props: AuthInputProps): JSX.Element {
	const {
		value,
		onChangeText,
		placeholder,
		hasError,
		disabled,
		autoFocus,
		autoCapitalize,
		keyboardType,
		maxLength,
		secureTextEntry,
		textContentType,
		accessibilityLabel,
	} = props;

	// Read NativeWind's runtime colour-scheme (not RN's
	// `Appearance.getColorScheme()`) so the placeholder colour
	// tracks whatever the app set programmatically via
	// `colorScheme.set(...)`. On macOS the auth flow is forced to
	// dark at boot (apps/desktop/index.js); RN's
	// `Appearance.getColorScheme()` is unreliable at first paint on
	// RN-macOS 0.73 and would have washed out the placeholder against
	// the dark surface.
	const { colorScheme: scheme } = useColorScheme();
	const placeholderTextColor =
		tokens.color.semantic[scheme === 'dark' ? 'dark' : 'light'].text.secondary;

	const borderClass = hasError
		? 'border-light-accent-rose dark:border-dark-accent-rose'
		: 'border-light-border-subtle dark:border-dark-border-subtle focus:border-light-accent-brand dark:focus:border-dark-accent-brand';

	// macOS's NSTextField top-anchors single-line text inside a fixed-
	// height field, so any direct `h-[44px]` on the `<TextInput>` makes
	// the value + placeholder hug the top edge. Wrap in a flex View
	// that owns the height + border + bg, and let the TextInput inside
	// auto-size to its glyph height plus a fixed vertical padding. The
	// wrapper's `justify-center` then vertically centres the text-box
	// natively on every platform. RN-Web flattens this back to one
	// `<div><input/></div>` pair which `<input>` already centres.
	return (
		<View
			className={`w-full h-[44px] rounded-[8px] border ${borderClass} bg-light-surface-panel dark:bg-dark-surface-panel flex-row items-center px-md`}
		>
			<TextInput
				value={value}
				onChangeText={onChangeText}
				placeholder={placeholder}
				// `placeholderTextColor` is the only way to colour the
				// placeholder on react-native primitives — Tailwind's
				// `placeholder:` modifier doesn't reach the underlying RN
				// native input layer, so we read the token at render time.
				placeholderTextColor={placeholderTextColor}
				editable={!disabled}
				autoFocus={autoFocus}
				autoCapitalize={autoCapitalize}
				autoCorrect={false}
				keyboardType={keyboardType}
				maxLength={maxLength}
				secureTextEntry={secureTextEntry}
				textContentType={textContentType}
				accessibilityLabel={accessibilityLabel}
				{...({ enableFocusRing: false } as { enableFocusRing: boolean })}
				className="flex-1 font-sans font-normal text-[16px] text-light-text-primary dark:text-dark-text-primary outline-none"
				// `padding: 0` removes NSTextField's default top inset
				// (~2px on macOS) so the wrapper's `items-center` is
				// the only thing positioning the glyph vertically.
				style={{ padding: 0 }}
			/>
		</View>
	);
}

// ── Primary button ──────────────────────────────────────────────────

export interface AuthPrimaryButtonProps {
	readonly onPress: () => void;
	readonly disabled?: boolean;
	readonly children: ReactNode;
	readonly accessibilityLabel?: string;
}

export function AuthPrimaryButton({
	onPress,
	disabled,
	children,
	accessibilityLabel,
}: AuthPrimaryButtonProps): JSX.Element {
	const isDisabled = Boolean(disabled);
	const bgClass = isDisabled
		? 'bg-light-surface-elevated dark:bg-dark-surface-elevated'
		: 'bg-light-accent-brand dark:bg-dark-accent-brand';
	const textClass = isDisabled
		? 'text-light-text-disabled dark:text-dark-text-disabled'
		: 'text-light-text-onAccent dark:text-dark-text-onAccent';
	return (
		<Pressable
			onPress={onPress}
			disabled={isDisabled}
			accessibilityRole="button"
			accessibilityLabel={accessibilityLabel}
			accessibilityState={{ disabled: isDisabled }}
			className={`w-full h-[44px] rounded-[8px] items-center justify-center ${bgClass}`}
		>
			<Text
				className={`font-sans font-bold text-[16px] leading-[22px] ${textClass}`}
			>
				{children}
			</Text>
		</Pressable>
	);
}

// ── Link button (Forgot password? / Resend code / Sign up) ──────────

export interface AuthLinkButtonProps {
	readonly onPress: () => void;
	readonly children: ReactNode;
	readonly fontSize?: 14 | 16;
	readonly accessibilityLabel?: string;
}

export function AuthLinkButton({
	onPress,
	children,
	fontSize = 16,
	accessibilityLabel,
}: AuthLinkButtonProps): JSX.Element {
	const sizeClass =
		fontSize === 14 ? 'text-[14px] leading-[20px]' : 'text-[16px] leading-[22px]';
	return (
		<Pressable
			onPress={onPress}
			accessibilityRole="link"
			accessibilityLabel={accessibilityLabel}
		>
			<Text
				className={`font-sans font-normal ${sizeClass} text-light-accent-brand dark:text-dark-accent-brand`}
			>
				{children}
			</Text>
		</Pressable>
	);
}

// ── Cross-link footer ───────────────────────────────────────────────

export interface AuthCrossLinkFooterProps {
	readonly prompt: string;
	readonly actionLabel: string;
	readonly onPressAction: () => void;
}

export function AuthCrossLinkFooter({
	prompt,
	actionLabel,
	onPressAction,
}: AuthCrossLinkFooterProps): JSX.Element {
	return (
		<View className="flex-row items-center justify-center gap-xs mt-xl">
			<Text className="font-sans font-normal text-[16px] leading-[22px] text-light-text-secondary dark:text-dark-text-secondary">
				{prompt}
			</Text>
			<AuthLinkButton onPress={onPressAction}>{actionLabel}</AuthLinkButton>
		</View>
	);
}

// ── Form column wrapper (kept for backward-compat) ──────────────────

export function AuthForm({ children }: { readonly children: ReactNode }): JSX.Element {
	return <View className="flex-col gap-md">{children}</View>;
}
